import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Images, Loader2, Plus, Save, Upload, X } from 'lucide-react'

import {
  createBibleCharacter,
  createBibleCostume,
  createBibleLocation,
  createBibleProp,
  patchBibleCharacter,
  patchBibleCostume,
  patchBibleLocation,
  patchBibleProp,
  saveBibleStyle,
  uploadProductionAsset,
  type BibleStyle,
} from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { assetMediaUrl, resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { AssetMultiPickerModal } from '@/features/studio/modals/AssetMultiPickerModal'
import type { BibleEntryKind, BibleEntryLike } from '@/features/studio/pages/bible/bible-utils'
import {
  bibleAllowsMultipleImages,
  bibleKindLabelKey,
  bibleReferenceUrls,
  bibleShowsSeriesOutline,
  styleFieldNameKey,
  styleFieldPlaceholderKey,
  descriptionPlaceholderKey,
  uploadAssetKindTag,
  type BibleStyleField,
} from '@/features/studio/pages/bible/bible-utils'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoTextarea, ProtoThumb } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

interface PendingImage {
  id: string
  url?: string
  file?: File
}

export function BibleEntryModal({
  open,
  onClose,
  productionId,
  kind,
  seriesOutline,
  editing,
  styleSnapshot,
  styleField,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  kind: BibleEntryKind
  seriesOutline: string
  editing: BibleEntryLike | null
  styleSnapshot: BibleStyle | null
  styleField: BibleStyleField | null
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [images, setImages] = useState<PendingImage[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isStyle = kind === 'style'
  const isEdit = editing != null
  const activeStyleField = isStyle ? styleField : null
  const multipleImages = bibleAllowsMultipleImages(kind)
  const showSeriesOutline = bibleShowsSeriesOutline(kind)
  const kindLabel = t(bibleKindLabelKey(kind))
  const styleFieldLabel = activeStyleField ? t(styleFieldNameKey(activeStyleField)) : kindLabel
  const showStyleArtImage = isStyle && activeStyleField === 'art_direction'
  const showEntryImages = !isStyle
  const twoColumn = showSeriesOutline || showStyleArtImage || showEntryImages

  useEffect(() => {
    if (!open) return
    setError(null)
    if (isStyle && activeStyleField) {
      setName(t(styleFieldNameKey(activeStyleField)))
      setDescription((styleSnapshot?.[activeStyleField] ?? '').trim())
      const urls =
        activeStyleField === 'art_direction' ? bibleReferenceUrls(styleSnapshot ?? {}) : []
      setImages(
        urls.slice(0, 1).map((url, i) => ({
          id: `existing-${i}`,
          url,
        })),
      )
      return
    }
    setName(editing?.name ?? '')
    setDescription(editing?.description ?? '')
    const urls = bibleReferenceUrls(editing ?? {})
    setImages(
      (multipleImages ? urls : urls.slice(0, 1)).map((url, i) => ({
        id: `existing-${i}`,
        url,
      })),
    )
  }, [open, editing, isStyle, activeStyleField, multipleImages, styleSnapshot, t])

  function removeImage(id: string) {
    setImages((prev) => prev.filter((img) => img.id !== id))
  }

  function addFiles(files: FileList | null) {
    if (!files?.length) return
    const next = Array.from(files).map((file) => ({
      id: `file-${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
    }))
    setImages((prev) => (multipleImages ? [...prev, ...next] : next.slice(0, 1)))
  }

  function addAssetUrls(urls: string[]) {
    const incoming = urls.filter(Boolean)
    if (!incoming.length) return
    setImages((prev) => {
      if (multipleImages) {
        const existing = new Set(prev.map((img) => img.url).filter(Boolean))
        const additions = incoming
          .filter((url) => !existing.has(url))
          .map((url, i) => ({ id: `asset-${Date.now()}-${i}`, url }))
        return [...prev, ...additions]
      }
      return [{ id: `asset-${Date.now()}`, url: incoming[0] }]
    })
  }

  async function resolveImageUrls(): Promise<string[]> {
    const urls: string[] = []
    const pendingUploads = images.filter((img) => img.file && !img.url)
    if (pendingUploads.length > 0) setUploading(true)
    try {
      for (const img of images) {
        if (img.url) {
          urls.push(img.url)
          continue
        }
        if (!img.file) continue
        const asset = await uploadProductionAsset(productionId, img.file, {
          name: name.trim() || t('bible.untitledImage'),
          kind: uploadAssetKindTag(kind),
        })
        const url = assetMediaUrl(asset)
        if (url) urls.push(url)
      }
    } finally {
      if (pendingUploads.length > 0) setUploading(false)
    }
    return multipleImages ? urls : urls.slice(0, 1)
  }

  async function saveEntry(body: {
    name: string
    description: string
    reference_urls: string[]
    reference_url: string
  }) {
    if (isStyle && activeStyleField) {
      const current = styleSnapshot ?? {
        art_direction: '',
        color_palette: '',
        style_prompt: '',
        negative_prompt: '',
        reference_urls: [],
        updated_at: '',
      }
      await saveBibleStyle(productionId, {
        art_direction: current.art_direction,
        color_palette: current.color_palette,
        style_prompt: current.style_prompt,
        negative_prompt: current.negative_prompt,
        reference_urls: current.reference_urls ?? [],
        [activeStyleField]: body.description.trim(),
        ...(activeStyleField === 'art_direction'
          ? { reference_urls: body.reference_urls }
          : {}),
      })
      return
    }
    if (isEdit && editing) {
      if (kind === 'character') await patchBibleCharacter(productionId, editing.public_id, body)
      else if (kind === 'location') await patchBibleLocation(productionId, editing.public_id, body)
      else if (kind === 'prop') await patchBibleProp(productionId, editing.public_id, body)
      else if (kind === 'costume') await patchBibleCostume(productionId, editing.public_id, body)
      return
    }
    if (kind === 'character') await createBibleCharacter(productionId, body)
    else if (kind === 'location') await createBibleLocation(productionId, body)
    else if (kind === 'prop') await createBibleProp(productionId, body)
    else if (kind === 'costume') await createBibleCostume(productionId, body)
  }

  async function handleSubmit() {
    const trimmedName = name.trim()
    if (!isStyle && !trimmedName) {
      setError(t('bible.nameRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const reference_urls =
        showStyleArtImage || showEntryImages ? await resolveImageUrls() : []
      await saveEntry({
        name: trimmedName,
        description: description.trim(),
        reference_urls,
        reference_url: reference_urls[0] ?? '',
      })
      onSaved()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'bible.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const modalTitle = isEdit
    ? t('bible.editEntry', { kind: isStyle ? styleFieldLabel : kindLabel })
    : t('bible.addEntry', { kind: isStyle ? styleFieldLabel : kindLabel })
  const selectedAssetUrls = images.map((img) => img.url).filter((url): url is string => Boolean(url))

  return (
    <>
      <ProtoModal
        open={open}
        onClose={onClose}
        title={modalTitle}
        size="xl"
        footer={
          <>
            <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={saving || uploading}>
              {t('common.cancel')}
            </ProtoBtn>
            <ProtoBtn size="sm" disabled={saving || uploading} onClick={() => void handleSubmit()}>
              {saving || uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? (
                <Save className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {isEdit ? t('bible.save') : t('bible.create')}
            </ProtoBtn>
          </>
        }
      >
        {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
        <div className={`grid gap-5 ${twoColumn ? 'lg:grid-cols-2' : ''}`}>
          {showEntryImages || showStyleArtImage ? (
            <div>
              <ProtoLabel>{multipleImages ? t('bible.modalImages') : t('bible.modalImage')}</ProtoLabel>
              {multipleImages ? (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {images.map((img) => (
                    <ImageTile key={img.id} img={img} onRemove={() => removeImage(img.id)} />
                  ))}
                  <UploadTile onClick={() => fileRef.current?.click()} label={t('bible.uploadImages')} />
                </div>
              ) : (
                <div className="mt-2">
                  {images[0] ? (
                    <div className="relative aspect-[4/3] max-w-xs overflow-hidden rounded-xl border border-white/[0.06] bg-[#0e0e16]">
                      <ImageTile
                        img={images[0]}
                        onRemove={() => removeImage(images[0].id)}
                        className="h-full w-full rounded-xl"
                      />
                    </div>
                  ) : (
                    <UploadTile
                      onClick={() => fileRef.current?.click()}
                      label={t('bible.uploadImage')}
                      className="aspect-[4/3] max-w-xs w-full"
                    />
                  )}
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple={multipleImages}
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files)
                  e.target.value = ''
                }}
              />
              <ProtoBtn size="sm" variant="secondary" className="mt-3" onClick={() => setPickerOpen(true)}>
                <Images className="h-4 w-4" />
                {t('bible.pickFromAssets')}
              </ProtoBtn>
              <p className="mt-2 text-[11px] text-zinc-600">
                {multipleImages ? t('bible.modalImagesHint') : t('bible.modalImageHint')}
              </p>
            </div>
          ) : null}

          <div className="flex min-h-0 flex-col gap-4">
            {!isStyle ? (
              <div>
                <ProtoLabel required>{t('bible.fieldName')}</ProtoLabel>
                <ProtoInput value={name} onChange={(e) => setName(e.target.value)} placeholder={t('bible.newNamePlaceholder')} />
              </div>
            ) : null}
            {showSeriesOutline ? (
              <div>
                <ProtoLabel>{t('bible.seriesOutlineRef')}</ProtoLabel>
                <div className="studio-scrollbar-hidden mt-1 max-h-36 overflow-y-auto rounded-xl border border-border bg-muted/30 p-3 text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground">
                  {seriesOutline.trim() ? seriesOutline : t('bible.seriesOutlineEmpty')}
                </div>
              </div>
            ) : null}
            <div className="flex min-h-0 flex-1 flex-col">
              <ProtoLabel>{t('bible.modalContent')}</ProtoLabel>
              <ProtoTextarea
                rows={showSeriesOutline ? 8 : 10}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  isStyle && activeStyleField
                    ? t(styleFieldPlaceholderKey(activeStyleField))
                    : t(descriptionPlaceholderKey(kind))
                }
                className="mt-1 min-h-[160px] flex-1"
              />
            </div>
          </div>
        </div>
      </ProtoModal>

      <AssetMultiPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        productionId={productionId}
        selectedUrls={selectedAssetUrls}
        maxSelect={multipleImages ? undefined : 1}
        onConfirm={(urls) => {
          addAssetUrls(urls)
          setPickerOpen(false)
        }}
      />
    </>
  )
}

function ImageTile({
  img,
  onRemove,
  className,
}: {
  img: PendingImage
  onRemove: () => void
  className?: string
}) {
  return (
    <div className={`relative aspect-square overflow-hidden rounded-xl border border-white/[0.06] bg-[#0e0e16] ${className ?? ''}`}>
      {img.url ? (
        <img src={resolveMediaDisplayUrl(img.url)} alt="" className="h-full w-full object-cover" />
      ) : img.file ? (
        <img src={URL.createObjectURL(img.file)} alt="" className="h-full w-full object-cover" />
      ) : (
        <ProtoThumb seed={img.id} aspect="square" className="h-full w-full" />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={onRemove}
        className="absolute right-1 top-1 rounded-md bg-black/60 text-zinc-200 hover:bg-black/80"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

function UploadTile({
  onClick,
  label,
  className,
}: {
  onClick: () => void
  label: string
  className?: string
}) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      className={`flex aspect-square h-auto flex-col items-center justify-center gap-1 rounded-xl border-dashed border-white/[0.12] bg-[#0e0e16] text-zinc-500 hover:border-indigo-500/30 hover:text-indigo-200 ${className ?? ''}`}
    >
      <Upload className="h-5 w-5" />
      <span className="text-[10px]">{label}</span>
    </Button>
  )
}
